'use client';
import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Loader2, CheckCircle2 } from 'lucide-react';
import Link from 'next/link';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (user: { name: string; email: string }) => void;
  /** Optional message shown above the form (e.g. "Please log in before joining a session") */
  promptMessage?: string;
}

export default function LoginModal({ isOpen, onClose, onSuccess, promptMessage }: LoginModalProps) {
  const { t } = useLanguage();
  const { signIn, signUp } = useAuth();
  const [tab, setTab] = useState<'login' | 'register'>('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [authError, setAuthError] = useState('');
  const [toast, setToast] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setPassword('');
      setName('');
      setAuthError('');
      setToast(null);
      setTab('login');
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setLoading(true);
    try {
      if (tab === 'login') {
        const data = await signIn(email, password);
        const displayName = data?.user?.user_metadata?.display_name || name || 'Runner';
        showToast('success', t('Welcome back.', '欢迎回来。'));
        onSuccess?.({ name: displayName, email });
        setTimeout(() => onClose(), 1200);
      } else {
        await signUp(email, password, { displayName: name });
        showToast('success', t(
          'Account created. You can now access your member area.',
          '账户已创建，你现在可以进入会员中心。'
        ));
        onSuccess?.({ name: name || 'Runner', email });
        setTimeout(() => onClose(), 1800);
      }
    } catch (err: any) {
      const msg = err?.message || t('Something went wrong. Please try again.', '出现错误，请重试。');
      setAuthError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground/40 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />
      <div className="relative bg-card rounded-2xl shadow-modal w-full max-w-md animate-slide-up overflow-hidden">
        {/* Toast */}
        {toast && (
          <div
            style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              zIndex: 10, display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 600,
              backgroundColor: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
              color: toast.type === 'success' ? '#15803d' : '#b91c1c',
              border: `1px solid ${toast.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
            }}
          >
            {toast.type === 'success' && <CheckCircle2 size={15} />}
            {toast.msg}
          </div>
        )}

        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-secondary transition-colors"
        >
          <X size={18} className="text-muted-foreground" />
        </button>

        <div className="p-8">
          {/* Brand */}
          <div className="mb-5">
            <p className="font-brand-italic text-2xl text-foreground">R3SET Running</p>
            <p className="text-sm text-muted-foreground mt-1">
              {t('Account access for members.', '会员账户登录。')}
            </p>
          </div>

          {/* Prompt message (e.g. from join session) */}
          {promptMessage && (
            <div
              style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 8,
                backgroundColor: '#fffbeb', border: '1px solid #fde68a',
                fontSize: 13, color: '#92400e', lineHeight: 1.5,
              }}
            >
              {promptMessage}
            </div>
          )}

          {/* Tabs */}
          <div className="flex border-b border-border mb-6">
            {(['login', 'register'] as const).map((tabKey) => (
              <button
                key={`modal-tab-${tabKey}`}
                onClick={() => { setTab(tabKey); setAuthError(''); }}
                className={`pb-2.5 mr-6 text-sm font-semibold transition-colors border-b-2 -mb-px ${
                  tab === tabKey
                    ? 'border-foreground text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tabKey === 'login' ? t('Login', '登录') : t('Register', '注册')}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            {tab === 'register' && (
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">
                  {t('Name', '姓名')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t('Your name', '你的姓名')}
                  className="input-field"
                  required
                />
              </div>
            )}
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                {t('Email', '邮箱')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="input-field"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                {t('Password', '密码')}
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="input-field pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {authError && (
              <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {authError}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full justify-center mt-2"
              style={{ minWidth: '140px' }}
            >
              {loading ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                tab === 'login' ? t('Login', '登录') : t('Create Account', '创建账户')
              )}
            </button>

            {/* Secondary link: new users should apply first */}
            <p className="text-xs text-center text-muted-foreground mt-1">
              {t('New here?', '第一次加入？')}{' '}
              <Link
                href="/apply"
                onClick={onClose}
                className="underline hover:text-foreground transition-colors"
              >
                {t('Apply to join the community first.', '请先提交社区申请。')}
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}